import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";

// API
import { getAllTicketsForSuperAdmin, updateTicketStatus } from "services/api";

// Modal Styles
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 4,
  outline: "none",
  maxHeight: "90vh",
  overflowY: "auto",
};

function SupportTicketsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Redirect if no company context
  useEffect(() => {
    if (!state?.company) navigate("/super-admin");
  }, [state, navigate]);

  const company = state?.company || {};

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [triageData, setTriageData] = useState({ adminSeverity: 3, status: "OPEN" });
  const [updating, setUpdating] = useState(false);

  // Fetch Data
  const fetchTickets = async () => {
    setLoading(true);
    if (company.tenant) {
      const data = await getAllTicketsForSuperAdmin(company.tenant);
      setTickets(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTickets();
  }, [company]);

  // Handle Opening Modal
  const openTriageModal = (ticket) => {
    setSelectedTicket(ticket);
    setTriageData({
      adminSeverity: ticket.adminSeverity || 3, // Default to Medium (3) if not set
      status: ticket.status || "OPEN",
    });
  };

  // Handle Saving Changes
  const handleSaveChanges = async () => {
    setUpdating(true);
    const res = await updateTicketStatus(
      company.tenant,
      selectedTicket.id,
      triageData.adminSeverity,
      triageData.status
    );

    if (res !== "error") {
      // Update local state locally to avoid full reload
      setTickets((prev) =>
        prev.map((t) =>
          t.id === selectedTicket.id
            ? { ...t, adminSeverity: triageData.adminSeverity, status: triageData.status }
            : t
        )
      );
      setSelectedTicket(null); // Close modal
    } else {
      alert("Failed to update ticket.");
    }
    setUpdating(false);
  };

  // Helpers for UI
  const getSeverityLabel = (level) => {
    switch (level) {
      case 1:
        return { label: "CRITICAL", color: "error" };
      case 2:
        return { label: "HIGH", color: "warning" };
      case 3:
        return { label: "MEDIUM", color: "info" };
      case 4:
        return { label: "LOW", color: "success" };
      case 5:
        return { label: "TRIVIAL", color: "secondary" };
      default:
        return { label: "PENDING", color: "light" };
    }
  };

  const getStatusColor = (status) => {
    if (status === "OPEN") return "info";
    if (status === "IN_PROGRESS") return "warning";
    if (status === "RESOLVED") return "success";
    if (status === "REJECTED") return "error";
    return "secondary"; // REJECTED
  };

  // Table Columns
  const columns = [
    { Header: "Date", accessor: "date", width: "15%" },
    { Header: "Type", accessor: "type", width: "10%" },
    { Header: "Subject", accessor: "subject", width: "25%" },
    { Header: "Reported By", accessor: "reporter", width: "15%" },
    { Header: "Admin Level", accessor: "severity", width: "15%", align: "center" },
    { Header: "Status", accessor: "status", width: "10%", align: "center" },
    { Header: "Action", accessor: "action", width: "10%", align: "center" },
  ];

  const rows = tickets.map((t) => {
    const sev = getSeverityLabel(t.adminSeverity);
    return {
      date: (
        <MDTypography variant="caption">{new Date(t.createdAt).toLocaleDateString()}</MDTypography>
      ),
      type: (
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <Icon color={t.type === "BUG" ? "error" : "info"} fontSize="small">
            {t.type === "BUG" ? "bug_report" : "lightbulb"}
          </Icon>
          <MDTypography variant="caption" fontWeight="bold">
            {t.type === "BUG" ? "Bug" : "Feature"}
          </MDTypography>
        </MDBox>
      ),
      subject: (
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{
            display: "block",
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t.subject}
        </MDTypography>
      ),
      reporter: (
        <MDBox display="flex" flexDirection="column">
          <MDTypography variant="caption" fontWeight="bold">
            {t.employeeName}
          </MDTypography>
          <MDBadge
            badgeContent={t.userSeverity}
            color="secondary"
            variant="contained"
            size="xs"
            container
          />
        </MDBox>
      ),
      severity: (
        <MDBadge
          badgeContent={sev.label}
          color={sev.color}
          variant="gradient"
          size="sm"
          container
        />
      ),
      status: (
        <MDBadge
          badgeContent={t.status}
          color={getStatusColor(t.status)}
          variant="gradient"
          size="sm"
          container
        />
      ),
      action: (
        <MDButton variant="text" color="info" onClick={() => openTriageModal(t)}>
          Details
        </MDButton>
      ),
    };
  });

  return (
    <MDBox py={3}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Support Tickets: {company.companyName}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total: {tickets.length} | Open: {tickets.filter((t) => t.status === "OPEN").length}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={fetchTickets}
            startIcon={<Icon>refresh</Icon>}
          >
            Refresh
          </MDButton>
        </MDBox>
      </Card>

      {/* Ticket Table */}
      <Card>
        <MDBox p={3}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" p={5}>
              <CircularProgress color="info" />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={false}
            />
          )}
        </MDBox>
      </Card>

      {/* Triage Modal */}
      <Modal open={Boolean(selectedTicket)} onClose={() => setSelectedTicket(null)}>
        <MDBox sx={modalStyle}>
          {selectedTicket && (
            <>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h5" fontWeight="medium">
                  {selectedTicket.subject}
                </MDTypography>
                <IconButton onClick={() => setSelectedTicket(null)}>
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>

              <Divider />

              <Grid container spacing={3}>
                {/* LEFT: Details */}
                <Grid item xs={12} md={7}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    DESCRIPTION
                  </MDTypography>
                  <MDBox bgcolor="#f8f9fa" p={2} borderRadius="lg" mb={2} border="1px solid #eee">
                    <MDTypography variant="body2">{selectedTicket.description}</MDTypography>
                  </MDBox>

                  {selectedTicket.type === "BUG" && (
                    <>
                      <MDTypography variant="caption" fontWeight="bold" color="text">
                        REPLICATION STEPS
                      </MDTypography>
                      <MDBox
                        bgcolor="#fff3e0"
                        p={2}
                        borderRadius="lg"
                        mb={2}
                        border="1px solid #ffe0b2"
                      >
                        <MDTypography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                          {selectedTicket.replicationSteps || "None provided."}
                        </MDTypography>
                      </MDBox>
                    </>
                  )}
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    PAGE URL
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    href={selectedTicket.pageUrl}
                    target="_blank"
                    color="info"
                    display="block"
                  >
                    {selectedTicket.pageUrl}
                  </MDTypography>
                </Grid>

                {/* RIGHT: Triage Controls */}
                <Grid item xs={12} md={5}>
                  <Card sx={{ border: "1px solid #ddd", boxShadow: "none" }}>
                    <MDBox p={2} bgcolor="#f8f9fa">
                      <MDTypography variant="h6">Admin Triage</MDTypography>
                    </MDBox>
                    <MDBox p={2} display="flex" flexDirection="column" gap={2}>
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="bold">
                          USER SEVERITY
                        </MDTypography>
                        <MDTypography variant="body2" color="error">
                          {selectedTicket.userSeverity}
                        </MDTypography>
                      </MDBox>

                      <FormControl fullWidth>
                        <InputLabel>Admin Severity Level</InputLabel>
                        <Select
                          value={triageData.adminSeverity}
                          label="Admin Severity Level"
                          onChange={(e) =>
                            setTriageData({ ...triageData, adminSeverity: e.target.value })
                          }
                          sx={{ height: 45 }}
                        >
                          <MenuItem value={1}>1 - CRITICAL (System Down)</MenuItem>
                          <MenuItem value={2}>2 - HIGH (Major Function)</MenuItem>
                          <MenuItem value={3}>3 - MEDIUM (Standard)</MenuItem>
                          <MenuItem value={4}>4 - LOW (Minor)</MenuItem>
                          <MenuItem value={5}>5 - TRIVIAL (Cosmetic)</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Ticket Status</InputLabel>
                        <Select
                          value={triageData.status}
                          label="Ticket Status"
                          onChange={(e) => setTriageData({ ...triageData, status: e.target.value })}
                          sx={{ height: 45 }}
                        >
                          <MenuItem value="OPEN">Open</MenuItem>
                          <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                          <MenuItem value="RESOLVED">Resolved</MenuItem>
                          <MenuItem value="REJECTED">Rejected</MenuItem>
                        </Select>
                      </FormControl>

                      <MDButton
                        variant="gradient"
                        color="success"
                        fullWidth
                        onClick={handleSaveChanges}
                        disabled={updating}
                      >
                        {updating ? "Saving..." : "Update Ticket"}
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </MDBox>
      </Modal>
    </MDBox>
  );
}

export default SupportTicketsPage;
